import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Moon, Sun, LayoutDashboard, GraduationCap } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { HealthCheck } from "@/components/HealthCheck";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface TopBarProps {
  user: User;
}

export function TopBar({ user }: TopBarProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { user: authUser } = useAuth();

  const isAdminView = location.pathname.startsWith('/dashboard') ||
                      location.pathname.startsWith('/students') ||
                      location.pathname.startsWith('/progress') ||
                      location.pathname.startsWith('/analytics') ||
                      location.pathname.startsWith('/chat') ||
                      location.pathname.startsWith('/videos') ||
                      location.pathname.startsWith('/escalations');
  const canSwitchView = authUser?.role === 'admin' || authUser?.role === 'educator';
  const switchButtonLabel = isAdminView ? "学習者ページへ移動" : "管理者ページへ戻る";

  const handleViewSwitch = () => {
    if (isAdminView) {
      navigate('/student/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();

    if (error) {
      toast({
        title: "エラー",
        description: "ログアウトに失敗しました",
        variant: "destructive",
      });
    } else {
      toast({
        title: "ログアウト成功",
        description: "またお会いしましょう",
      });
      navigate("/auth");
    }
  };

  return (
    <header className="h-14 border-b flex items-center justify-between px-4 bg-background">
      <div className="flex items-center gap-2">
        <SidebarTrigger />
      </div>

      <div className="flex items-center gap-4">
        <HealthCheck audience="admin" />

        {canSwitchView && (
          <Button
            variant={isAdminView ? "default" : "outline"}
            size="sm"
            onClick={handleViewSwitch}
            className="gap-2"
            aria-label={switchButtonLabel}
          >
            {isAdminView ? (
              <>
                <GraduationCap className="h-4 w-4" />
                <span className="hidden sm:inline">{switchButtonLabel}</span>
              </>
            ) : (
              <>
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden sm:inline">{switchButtonLabel}</span>
              </>
            )}
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={theme === "dark" ? "ダークモード（クリックで変更）" : "ライトモード（クリックで変更）"}
            >
              {theme === "dark" ? (
                <Moon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Sun className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")}>
              ライトモード
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              ダークモード
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              システム設定
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <span className="text-sm text-muted-foreground hidden sm:inline">
          {user.email}
        </span>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          aria-label="ログアウト"
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
        </Button>
      </div>
    </header>
  );
}
